import isEqual from 'lodash/isEqual';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';

import {
  convertCurrencyStrToNum,
  formatAmountsWithCommas,
} from './globalHelpers';

const checkAlphaNumeric = (value) => {
  const re = /^[a-zA-Z0-9\s]+$/i;
  if (!re.test(value)) {
    return ({
      error: 'invalidInput',
      message: 'Must contain only letters and numbers',
    });
  }
  return false;
};

const isBlank = (value) =>
  isUndefined(value) || isNull(value) || value.length === 0 || isEmptyString(value);

const isEmptyString = (str) => {
  if (typeof str === 'string') {
    return (/^\s*$/).test(str);
  }
  return false;
};

const validateGreaterThanZero = (number) => {
  const convertedNumber = Number(number);
  if (isEqual(convertedNumber, NaN)) {
    return ({
      error: 'invalidValue',
      message: 'Invalid number',
    });
  }
  if (convertedNumber <= 0) {
    return ({
      error: 'invalidValue',
      message: 'Invalid value',
    });
  }
  return false;
};

const validateWholeNumber = (number) => {
  const convertedNumber = Number(number);
  if (isEqual(convertedNumber, NaN)) {
    return ({
      error: 'invalidValue',
      message: 'Invalid number',
    });
  }
  // Returns truthy array if there's only digits, otherwise returns null
  if (!number.toString().match(/^[\d]+$/g)) {
    return ({
      error: 'invalidValue',
      message: 'Invalid number',
    });
  }
  return false;
};

const validateString = (str) => {
  if (typeof str !== 'string') {
    return ({
      error: 'invalidValue',
      message: 'Invalid value',
    });
  }
  return false;
};

const validateLength = (value, { length, maxLength, minLength }) => {
  if (length && value.length !== length) {
    return ({
      error: 'invalidLength',
      message: `Invalid length. Must have a length of ${length}`,
    });
  }
  if (minLength && value.length < minLength) {
    return ({
      error: 'invalidLength',
      message: `Invalid length. Must have a length greater than or equal to ${minLength}`,
    });
  }
  if (maxLength && value.length > maxLength) {
    return ({
      error: 'invalidLength',
      message: `Invalid length. Must have a length less than or equal to ${maxLength}`,
    });
  }
  return false;
};

const validateLoanNumber = (value) =>
  validateGreaterThanZero(value) ||
  validateWholeNumber(value) ||
  validateLength(value, { length: 10 });

const validateSSN = (value) =>
  validateGreaterThanZero(value) ||
  validateWholeNumber(value) ||
  validateLength(value, { length: 4 });

const validateBankAccountNumber = (value) =>
  validateGreaterThanZero(value) ||
  validateLength(value, { minLength: 5 }) ||
  validateLength(value, { maxLength: 17 }) ||
  validateExtraSpaces(value);

const validateBankNameOnAccount = (value) => {
  const re = /^[a-zA-Z ]+$/i;
  if (!re.test(value)) {
    return ({
      error: 'invalidNameOnAccount',
      message: 'Invalid name. Must contain only letters and spaces',
    });
  }
  if (value.trim().length < 3) {
    return ({
      error: 'invalidNameOnAccount',
      message: 'Must be at least three characters',
    });
  }
  return validateExtraSpaces(value);
};

const validateBankRoutingNumber = (value) =>
  validateGreaterThanZero(value) ||
  validateLength(value, { length: 9 }) ||
  validateExtraSpaces(value);

const validatePostalCode = (value) =>
  validateLength(value, { maxLength: 9 });

const validateZipCode = (value) =>
  validateGreaterThanZero(value) ||
  validateWholeNumber(value) ||
  validateLength(value, { length: 5 });

const validateUsername = (value) => {
  if (!validateEmail(value)) {
    return ({
      error: 'invalidUsername',
      message: 'Invalid username. Cannot use email address.',
    });
  }
  const isInvalidLength = validateLength(value, { maxLength: 25, minLength: 5 });
  if (!isInvalidLength) return checkAlphaNumeric(value);
  return isInvalidLength;
};

const validatePhone = (phone) => {
  const pattern1 = /[0-9]{3}-[0-9]{3}-[0-9]{4}/; // 510-123-4567
  const pattern2 = /\([0-9]{3}\) [0-9]{3}-[0-9]{4}/; // (510) 123-4567
  const pattern3 = /\([0-9]{3}\)[0-9]{3}-[0-9]{4}/; // (510)123-4567
  const pattern4 = /^[0-9]{10}$/; // 5101234567
  const isValid = (
    pattern1.test(phone) ||
    pattern2.test(phone) ||
    pattern3.test(phone) ||
    pattern4.test(phone)
  );
  if (!isValid) {
    return {
      error: 'invalidPhone',
      message: 'Invalid phone number, ex. (555) 800-0000',
    };
  }
  return false;
};

const validateEmail = (value) => {
  // eslint-disable-next-line max-len
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (!re.test(value)) {
    return ({
      error: 'invalidEmail',
      message: 'Invalid email address, ex. jdoe@mail.com',
    });
  }
  return false;
};

const validateConcurrentFields = (value, { comparisonValue, name }) => {
  if (value !== comparisonValue) {
    return ({
      error: 'invalidMatch',
      message: `${name} do not match`,
    });
  }
  return false;
};

const validateExtraSpaces = (value) => (
  value.length !== value.trim().length
    ? ({
      error: 'invalidValue',
      message: 'Leading or trailing space(s) not allowed',
    })
    : false
);

const validateOldPassword = (value) => (
  !value.length
    ? ({
      error: 'invalidPassword',
      message: 'Current Password does not match existing password',
    })
    : false
);

const validatePassword = (value) => {
  const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{10,})/;
  if (!re.test(value)) {
    return ({
      error: 'invalidPassword',
      message:
      'Must contain at least 1 lowercase, 1 uppercase, 1 number and at least 10 characters long',
    });
  }
  return false;
};

const validateMaxEqual = (value, { maxEqual = 999999999 }) => {
  if (maxEqual !== undefined && value > maxEqual) {
    return {
      error: 'invalidCurrency',
      message: `Invalid value. Must be less than or equal to ${formatAmountsWithCommas(maxEqual)}`,
    };
  }
  return false;
};

const validateNumberBetween = (value, { isOnChange, max, min }) => {
  const errorObject = {
    error: 'invalidValue', // eslint-disable-next-line max-len
    message: `Invalid value. Must be between ${formatAmountsWithCommas(min)} and ${formatAmountsWithCommas(max)}`,
  };
  /**
   * We only want to validate the max value on change,
   * otherwise an < minimum error would display immediately on first input.
   */
  if (isOnChange) {
    if (value > max) {
      return errorObject;
    }
    return false;
  }
  if (value > max || value < min) {
    return errorObject;
  }
  return false;
};

const validateFileInput = (value) => {
  const { size, type } = value;
  const fileTypes = [
    'gif',
    'jpeg',
    'jpg',
    'pdf',
    'png',
    'tif',
    'tiff',
  ];
  // Mobile is not able to determine file type on frontend
  if (type && !fileTypes.includes(type.toLowerCase())) {
    return ({
      error: 'invalidFileType',
      message:
        'Must be one of the following file types: .gif, .jpeg, .jpg, .pdf, .png, .tif, .tiff',
    });
  }
  if (size >= 10485760) {
    return ({
      error: 'invalidFileSize',
      message: 'Image size exceeds 10 MB.',
    });
  }
  return false;
};

const validateCurrency = (value, { maxEqual }) => {
  const re = /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{2})?$/;
  const convertedNumber = convertCurrencyStrToNum(value);
  if (!re.test(value)) {
    return ({
      error: 'invalidCurrency',
      message: 'Invalid currency format',
    });
  }
  return validateMaxEqual(convertedNumber, { maxEqual });
};

const validateConfirmInput = (value, { comparisonValue, name }) => {
  if (value !== comparisonValue) {
    return ({
      error: 'invalidMatch',
      message: `${name} do not match`,
    });
  }
  return false;
};

const validateNumber = (number) => {
  const numberWithoutCommas = number.replace(/,/g, '');
  const convertedNumber = Number(numberWithoutCommas);
  if (isEqual(convertedNumber, NaN) || convertedNumber < 0 || number === 'Infinity') {
    return ({
      error: 'invalidValue',
      message: 'Invalid number',
    });
  }
  return false;
};

const validateApartment = (value) => {
  if (value) return validateLength(value, { maxLength: 30 }) || checkAlphaNumeric(value);
  return false;
};

const validateCity = (value) =>
  validateLength(value, { maxLength: 21 }) || checkAlphaNumeric(value);

const validateCompany = (value) => {
  if (value) return validateLength(value, { maxLength: 30 }) || checkAlphaNumeric(value);
  return false;
};

const validateAdditionalAddressField = (value) => {
  if (value) return validateLength(value, { maxLength: 30 }) || checkAlphaNumeric(value);
  return false;
};

const validateStreetAddress = (value) =>
  validateLength(value, { maxLength: 30 }) || checkAlphaNumeric(value);

const validatePassCode = (value) =>
  validateNumber(value) || validateLength(value, { length: 6 });

const validateMessage = (value, maxLength) =>
  isBlank(value) || isEmptyString(value) || !!validateLength(value, maxLength);

const validateAny = () => false;

export const validationDictionary = {
  accountNumber: validateBankAccountNumber,
  additionalAddressField: validateAdditionalAddressField,
  any: validateAny,
  apartment: validateApartment,
  city: validateCity,
  company: validateCompany,
  concurrentFields: validateConcurrentFields,
  confirmInput: validateConfirmInput,
  currency: validateCurrency,
  currencyOnChange: validateMaxEqual,
  email: validateEmail,
  fileInput: validateFileInput,
  helocDrawValue: validateNumberBetween,
  length: validateLength,
  loanNumber: validateLoanNumber,
  message: validateMessage,
  nameOnAccount: validateBankNameOnAccount,
  oldPassword: validateOldPassword,
  passCode: validatePassCode,
  password: validatePassword,
  phone: validatePhone,
  postalCode: validatePostalCode,
  routingNumber: validateBankRoutingNumber,
  ssnTin: validateSSN,
  streetAddress: validateStreetAddress,
  textInput: validateString,
  username: validateUsername,
  zipCode: validateZipCode,
};

export const validate = ({
  required,
  type,
  value,
  ...validationProps
}) => {
  if (required && isBlank(value)) {
    return ({
      error: 'isRequired',
      message: 'Required field',
    });
  }
  if (!Array.isArray(type)) {
    const validationFunction = validationDictionary[type];
    return validationFunction(value, validationProps);
  }
  // If multiple validationTypes, return the first error encountered or false if no errors
  return type.reduce((acc, validationType) => {
    if (acc) return acc;
    const validationFunction = validationDictionary[validationType];
    return validationFunction(value, validationProps);
  }, false);
};
